import type { AdSlotPlacement, BannerAd } from '@shopgnd/shared';
import { getAuthedDataClient, getPublicDataClient } from '@/lib/amplify-client';

const now = () => new Date().toISOString();

function mapBannerAd(row: Record<string, unknown>): BannerAd {
  return {
    id: String(row.id),
    placement: row.placement as AdSlotPlacement,
    slotId: row.slotId ? String(row.slotId) : undefined,
    imageUrl: String(row.imageUrl ?? ''),
    targetUrl: String(row.targetUrl ?? '/advertise'),
    altText: String(row.altText ?? 'Advertisement'),
    advertiserName: String(row.advertiserName ?? 'Sponsor'),
    vendorId: row.vendorId ? String(row.vendorId) : undefined,
    startsAt: String(row.startsAt),
    endsAt: String(row.endsAt),
    impressions: Number(row.impressions ?? 0),
    clicks: Number(row.clicks ?? 0),
    active: row.active !== false,
  };
}

function isActive(ad: BannerAd): boolean {
  if (!ad.active) return false;
  const t = Date.now();
  return t >= new Date(ad.startsAt).getTime() && t <= new Date(ad.endsAt).getTime();
}

async function listAllBannerAdRows(client: Awaited<ReturnType<typeof getPublicDataClient>>): Promise<BannerAd[]> {
  const rows: BannerAd[] = [];
  let nextToken: string | undefined;

  do {
    const res = await client.models.BannerAd.list(
      nextToken ? { nextToken, limit: 200 } : { limit: 200 },
    );
    if (res.errors?.length) {
      console.warn(
        '[bannerAds] list errors:',
        res.errors.map((e: { message?: string }) => e.message).join('; '),
      );
    }
    for (const row of (res.data ?? []) as Record<string, unknown>[]) {
      rows.push(mapBannerAd(row));
    }
    nextToken = (res as { nextToken?: string | null }).nextToken ?? undefined;
  } while (nextToken);

  return rows;
}

export async function listBannerAdsLive(): Promise<BannerAd[]> {
  try {
    const client = await getPublicDataClient();
    return listAllBannerAdRows(client);
  } catch (err) {
    console.warn('[bannerAds] fetch failed:', err);
    return [];
  }
}

export async function listActiveBannerAds(placements: AdSlotPlacement[]): Promise<BannerAd[]> {
  const set = new Set(placements);
  const all = await listBannerAdsLive();
  return all.filter((ad) => set.has(ad.placement) && isActive(ad));
}

export async function recordBannerClick(adId: string): Promise<void> {
  try {
    const client = await getAuthedDataClient();
    const existing = await client.models.BannerAd.get({ id: adId });
    if (!existing.data) return;
    const clicks = Number((existing.data as Record<string, unknown>).clicks ?? 0) + 1;
    await client.models.BannerAd.update({ id: adId, clicks });
  } catch {
    /* best-effort */
  }
}

export async function recordBannerImpression(adId: string): Promise<void> {
  try {
    const client = await getAuthedDataClient();
    const existing = await client.models.BannerAd.get({ id: adId });
    if (!existing.data) return;
    const impressions = Number((existing.data as Record<string, unknown>).impressions ?? 0) + 1;
    await client.models.BannerAd.update({ id: adId, impressions });
  } catch {
    /* best-effort */
  }
}

export type BannerAdDraft = {
  placement: AdSlotPlacement;
  imageUrl: string;
  targetUrl: string;
  altText: string;
  advertiserName: string;
  startsAt: string;
  endsAt: string;
  active: boolean;
  vendorId?: string;
  slotId?: string;
};

export async function listBannerAdsForAdmin(): Promise<BannerAd[]> {
  try {
    const client = await getAuthedDataClient();
    return listAllBannerAdRows(client);
  } catch (err) {
    console.warn('[bannerAds] admin list failed:', err);
    return [];
  }
}

export async function createBannerAdAdmin(draft: BannerAdDraft): Promise<BannerAd | null> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.BannerAd.create({
      placement: draft.placement,
      imageUrl: draft.imageUrl,
      targetUrl: draft.targetUrl,
      altText: draft.altText,
      advertiserName: draft.advertiserName,
      startsAt: draft.startsAt,
      endsAt: draft.endsAt,
      active: draft.active,
      vendorId: draft.vendorId,
      slotId: draft.slotId,
      impressions: 0,
      clicks: 0,
    });
    if (!res.data) return null;
    return mapBannerAd(res.data as Record<string, unknown>);
  } catch (err) {
    console.error('[bannerAds] create failed', err);
    return null;
  }
}

export async function updateBannerAdAdmin(id: string, draft: Partial<BannerAdDraft>): Promise<BannerAd | null> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.BannerAd.update({ id, ...draft });
    if (!res.data) return null;
    return mapBannerAd(res.data as Record<string, unknown>);
  } catch (err) {
    console.error('[bannerAds] update failed', err);
    return null;
  }
}

export async function deleteBannerAdAdmin(id: string): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    await client.models.BannerAd.delete({ id });
    return true;
  } catch (err) {
    console.error('[bannerAds] delete failed', err);
    return false;
  }
}

export { now as bannerNowIso };
